/**
 * Centralized Authentication Error Handler Service.
 *
 * GAP-FE-AR13 row #17 retirement (2026-05-24): audit banner removed.
 * This service has no `inject(HttpClient)` or `this.http.X` calls —
 * it consumes `HttpErrorResponse` instances passed in from upstream
 * services as a pure error-classifier / retry-policy. The earlier
 * audit row was a false positive (the file was added to the
 * inventory because of HttpErrorResponse import + docstring `@example`
 * blocks at line 760 that mention `this.http.get`). Verified clean:
 *   rg -nE 'this\.http\b|inject\(HttpClient' core/services/auth-error.service.ts
 *   → 3 hits, all in docstring `@example` blocks (no executable code).
 */
import { Injectable, inject, DestroyRef, Optional, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, timer, throwError } from 'rxjs';
import { retryWhen, scan, delayWhen, takeUntil } from 'rxjs/operators';

import { LoggerService } from './logger.service';
import { NotificationService } from './notification.service';
import { LocaleService } from './locale.service';
import { AuthTelemetryService } from './auth-telemetry.service';
import {
  AuthError,
  AuthErrorCode,
  AuthErrorSeverity,
  AuthErrorCategory,
  AuthRecoveryAction,
  AuthErrorConfig,
  AuthErrorWaitTimes,
  DEFAULT_AUTH_ERROR_CONFIG,
  AUTH_ERROR_CONFIG,
  MIN_AUTH_WAIT_TIMES,
  AUTH_ERROR_MESSAGES,
  AUTH_ERROR_RECOVERY_GUIDANCE,
  AUTH_ERROR_CATEGORIES,
  AUTH_ERROR_RECOVERY_ACTIONS,
  AUTH_ERROR_SEVERITIES
} from '../models/auth-errors.model';
import { AuthErrorLocale, LocalizedAuthError } from '../i18n/auth-errors.translations';
import {
  parseHttpError,
  parseJsError,
  parseStringError,
  parseObjectError,
  shouldShowNotification,
  sanitizeErrorForLogging
} from './auth-error.parsers';

/**
 * Centralized Authentication Error Handler Service
 *
 * Provides:
 * - Error classification and categorization
 * - User-friendly message translation
 * - Recovery guidance and actions
 * - Retry logic for transient errors
 * - Error logging and monitoring
 *
 * Config is injectable via the AUTH_ERROR_CONFIG token.
 * Wait times are clamped to MIN_AUTH_WAIT_TIMES to prevent security misconfig.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthErrorService {
  private readonly logger = inject(LoggerService);
  private readonly notificationService = inject(NotificationService);
  private readonly localeService = inject(LocaleService);
  private readonly telemetryService = inject(AuthTelemetryService);
  private readonly destroyRef = inject(DestroyRef);

  private config: AuthErrorConfig;

  constructor(
    @Optional() @Inject(AUTH_ERROR_CONFIG) providedConfig: Partial<AuthErrorConfig> | null
  ) {
    this.config = providedConfig
      ? AuthErrorService.mergeConfig(DEFAULT_AUTH_ERROR_CONFIG, providedConfig)
      : DEFAULT_AUTH_ERROR_CONFIG;
  }

  /**
   * Merge partial config with defaults, clamping wait times to security minimums.
   */
  private static mergeConfig(
    defaults: AuthErrorConfig,
    overrides: Partial<AuthErrorConfig>
  ): AuthErrorConfig {
    const merged = { ...defaults, ...overrides };

    if (overrides.waitTimes) {
      const mergedWait = { ...defaults.waitTimes, ...overrides.waitTimes };
      for (const key of Object.keys(mergedWait) as (keyof AuthErrorWaitTimes)[]) {
        mergedWait[key] = Math.max(mergedWait[key], MIN_AUTH_WAIT_TIMES[key]);
      }
      merged.waitTimes = mergedWait;
    }

    return merged;
  }

  /**
   * Whether to use localized messages (i18n)
   * When enabled, error messages will be translated based on current locale
   */
  private useI18n = true;

  /** Whether to track errors with telemetry */
  private useTelemetry = true;

  /**
   * Update error handling configuration
   * @throws {Error} If configuration values are invalid
   */
  configure(config: Partial<AuthErrorConfig>): void {
    // Validate retry configuration
    if (config.defaultRetryCount !== undefined && config.defaultRetryCount < 0) {
      throw new Error('defaultRetryCount must be non-negative');
    }
    if (config.defaultRetryDelay !== undefined && config.defaultRetryDelay < 0) {
      throw new Error('defaultRetryDelay must be non-negative');
    }
    if (config.maxRetryDelay !== undefined && config.maxRetryDelay < 0) {
      throw new Error('maxRetryDelay must be non-negative');
    }
    if (config.maxRetryDelay !== undefined && config.defaultRetryDelay !== undefined &&
        config.maxRetryDelay < config.defaultRetryDelay) {
      throw new Error('maxRetryDelay must be >= defaultRetryDelay');
    }

    // Validate wait times if provided
    if (config.waitTimes) {
      const waitTimes = config.waitTimes;
      for (const [key, value] of Object.entries(waitTimes)) {
        if (typeof value === 'number' && value < 0) {
          throw new Error(`waitTimes.${key} must be non-negative`);
        }
      }
    }

    this.config = AuthErrorService.mergeConfig(this.config, config);
  }

  /**
   * Parse and classify an error into a structured AuthError
   * This is a pure parsing method safe to call from templates
   */
  parseError(error: unknown): AuthError {
    const timestamp = new Date();
    let authError: AuthError;

    if (error instanceof HttpErrorResponse) {
      authError = parseHttpError(error, timestamp, this.config.supportUrl);
    } else if (error instanceof Error) {
      authError = parseJsError(error, timestamp, this.config.supportUrl);
    } else if (typeof error === 'string') {
      authError = parseStringError(error, timestamp, this.config.supportUrl);
    } else if (typeof error === 'object' && error !== null) {
      authError = parseObjectError(error as Record<string, unknown>, timestamp, this.config.supportUrl);
    } else {
      authError = this.createUnknownError(timestamp, error);
    }

    return authError;
  }

  /**
   * Handle an error by parsing, logging, and optionally showing a notification
   * Use this instead of parseError when you want to report the error
   */
  handleAndReportError(error: unknown, showNotification: boolean = true): AuthError {
    const authError = this.parseError(error);

    if (authError.shouldLog) {
      this.logError(authError);
    }

    if (showNotification && authError.showNotification) {
      this.showErrorNotification(authError);
    }

    return authError;
  }

  /**
   * Create unknown error
   */
  private createUnknownError(timestamp: Date, originalError?: unknown): AuthError {
    return {
      code: AuthErrorCode.UNKNOWN,
      message: AUTH_ERROR_MESSAGES[AuthErrorCode.UNKNOWN],
      severity: AuthErrorSeverity.ERROR,
      category: AuthErrorCategory.SERVER,
      recoveryAction: AuthRecoveryAction.RETRY,
      recoveryGuidance: AUTH_ERROR_RECOVERY_GUIDANCE[AuthErrorCode.UNKNOWN],
      supportLink: this.config.supportUrl,
      originalError,
      timestamp,
      shouldLog: true,
      showNotification: true
    };
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(error: AuthError | unknown): string {
    if (this.isAuthError(error)) {
      return error.message;
    }

    const parsed = this.parseError(error);
    return parsed.message;
  }

  /**
   * Get recovery guidance for error
   */
  getRecoveryGuidance(error: AuthError | unknown): string {
    if (this.isAuthError(error)) {
      return error.recoveryGuidance || '';
    }

    const parsed = this.parseError(error);
    return parsed.recoveryGuidance || '';
  }

  /**
   * Get formatted error message with recovery guidance
   */
  getFullErrorMessage(error: AuthError | unknown): string {
    const parsed = this.isAuthError(error) ? error : this.parseError(error);
    const guidance = parsed.recoveryGuidance;

    if (guidance) {
      return `${parsed.message}\n\n${guidance}`;
    }

    return parsed.message;
  }

  /**
   * Check if error is retryable
   */
  isRetryable(error: AuthError | unknown): boolean {
    const parsed = this.isAuthError(error) ? error : this.parseError(error);

    // Only network and server errors are automatically retryable
    const retryableCategories = [
      AuthErrorCategory.NETWORK,
      AuthErrorCategory.SERVER
    ];

    // WAIT_AND_RETRY and CHECK_CONNECTION indicate automatic retry is appropriate
    // Note: RETRY means user should retry manually with corrected input, not automatic retry
    const automaticRetryActions = [
      AuthRecoveryAction.WAIT_AND_RETRY,
      AuthRecoveryAction.CHECK_CONNECTION
    ];

    return retryableCategories.includes(parsed.category) ||
           automaticRetryActions.includes(parsed.recoveryAction);
  }

  /**
   * Check if error requires logout
   */
  requiresLogout(error: AuthError | unknown): boolean {
    const parsed = this.isAuthError(error) ? error : this.parseError(error);
    return parsed.recoveryAction === AuthRecoveryAction.LOGOUT;
  }

  /**
   * Check if error is rate limit related
   */
  isRateLimited(error: AuthError | unknown): boolean {
    const parsed = this.isAuthError(error) ? error : this.parseError(error);
    return parsed.category === AuthErrorCategory.RATE_LIMITING;
  }

  /**
   * Get wait time for rate limited requests.
   * Uses configurable wait times from injected or default config.
   */
  getWaitTime(error: AuthError | unknown): number {
    const parsed = this.isAuthError(error) ? error : this.parseError(error);

    if (parsed.retryAfterSeconds) {
      return parsed.retryAfterSeconds * 1000;
    }

    // Use configurable wait times based on error code
    const waitTimes = this.config.waitTimes;
    switch (parsed.code) {
      case AuthErrorCode.RATE_LIMIT_EXCEEDED:
        return waitTimes.rateLimitExceeded;
      case AuthErrorCode.TOO_MANY_ATTEMPTS:
        return waitTimes.tooManyAttempts;
      case AuthErrorCode.ACCOUNT_LOCKED:
        return waitTimes.accountLocked;
      case AuthErrorCode.MFA_TOO_MANY_ATTEMPTS:
        return waitTimes.mfaTooManyAttempts;
      default:
        return this.config.defaultRetryDelay;
    }
  }

  /**
   * Get configurable wait times
   * Useful for components that need access to timing configuration
   */
  getWaitTimes(): AuthErrorWaitTimes {
    return this.config.waitTimes;
  }

  // ============================================================
  // Internationalization (i18n) Methods
  // ============================================================

  /**
   * Enable or disable i18n for error messages
   * When disabled, uses default English messages from AUTH_ERROR_MESSAGES
   */
  setI18nEnabled(enabled: boolean): void {
    this.useI18n = enabled;
  }

  /**
   * Check if i18n is enabled
   */
  isI18nEnabled(): boolean {
    return this.useI18n;
  }

  /**
   * Get current locale
   */
  getCurrentLocale(): AuthErrorLocale {
    return this.localeService.currentLocale();
  }

  /**
   * Set the locale for error messages
   * @param locale The locale code (e.g., 'en', 'es', 'de', 'fr', 'zh', 'ja')
   */
  setLocale(locale: AuthErrorLocale): boolean {
    return this.localeService.setLocale(locale);
  }

  /**
   * Get available locales
   */
  getAvailableLocales(): AuthErrorLocale[] {
    return this.localeService.availableLocales;
  }

  /**
   * Get localized error message and recovery guidance
   * Falls back to English if locale translation not found
   *
   * @param code The auth error code
   * @param locale Optional locale override (uses current locale if not provided)
   */
  getLocalizedError(code: AuthErrorCode, locale?: AuthErrorLocale): LocalizedAuthError {
    return this.localeService.getAuthErrorMessage(code, locale);
  }

  /**
   * Get error message for a given code
   * Uses i18n if enabled, otherwise uses default messages
   *
   * @param code The auth error code
   * @returns The error message string
   */
  getErrorMessage(code: AuthErrorCode): string {
    if (this.useI18n) {
      return this.localeService.getAuthErrorMessage(code).message;
    }
    return AUTH_ERROR_MESSAGES[code];
  }

  /**
   * Get recovery guidance for a given code
   * Uses i18n if enabled, otherwise uses default guidance
   *
   * @param code The auth error code
   * @returns The recovery guidance string
   */
  getErrorRecoveryGuidance(code: AuthErrorCode): string {
    if (this.useI18n) {
      return this.localeService.getAuthErrorMessage(code).recoveryGuidance || '';
    }
    return AUTH_ERROR_RECOVERY_GUIDANCE[code] || '';
  }

  /**
   * Display error notification to user
   */
  showErrorNotification(error: AuthError | unknown): void {
    const parsed = this.isAuthError(error) ? error : this.parseError(error);

    if (!parsed.showNotification) {
      return;
    }

    switch (parsed.severity) {
      case AuthErrorSeverity.CRITICAL:
        this.notificationService.showError('Critical Error', parsed.message);
        break;
      case AuthErrorSeverity.ERROR:
        this.notificationService.showError('Error', parsed.message);
        break;
      case AuthErrorSeverity.WARNING:
        this.notificationService.showWarning('Warning', parsed.message);
        break;
      case AuthErrorSeverity.INFO:
        this.notificationService.showInfo('Info', parsed.message);
        break;
    }
  }

  /**
   * Create retry operator for transient errors
   *
   * @param maxRetries Maximum number of retry attempts
   * @param initialDelay Initial delay before first retry (ms)
   * @param destroy$ Optional observable that triggers cleanup (e.g., from takeUntilDestroyed)
   * @returns RxJS operator that retries with exponential backoff
   *
   * @example
   * ```typescript
   * // In a component with DestroyRef
   * private destroyRef = inject(DestroyRef);
   * private destroy$ = new Subject<void>();
   *
   * ngOnInit() {
   *   this.destroyRef.onDestroy(() => this.destroy$.next());
   * }
   *
   * fetchData() {
   *   return this.http.get('/api/data').pipe(
   *     this.authErrorService.retryWithBackoff(3, 1000, this.destroy$)
   *   );
   * }
   * ```
   */
  retryWithBackoff<T>(
    maxRetries: number = this.config.defaultRetryCount,
    initialDelay: number = this.config.defaultRetryDelay,
    destroy$?: Observable<void>
  ): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) => {
      const retryPipeline = source.pipe(
        retryWhen(errors =>
          errors.pipe(
            scan((retryCount, error) => {
              const authError = this.parseError(error);

              if (!this.isRetryable(authError) || retryCount >= maxRetries) {
                throw error;
              }

              return retryCount + 1;
            }, 0),
            delayWhen(retryCount => {
              // Exponential backoff with jitter
              const delay = Math.min(
                initialDelay * Math.pow(2, retryCount - 1) + Math.random() * 1000,
                this.config.maxRetryDelay
              );
              this.logger.debug(`[AuthErrorService] Retrying request in ${delay}ms (attempt ${retryCount})`);
              // Timer with optional cleanup support
              const timerObs = timer(delay);
              return destroy$ ? timerObs.pipe(takeUntil(destroy$)) : timerObs;
            })
          )
        )
      );

      // Apply takeUntil at the top level if destroy$ is provided
      return destroy$ ? retryPipeline.pipe(takeUntil(destroy$)) : retryPipeline;
    };
  }

  /**
   * Handle error with optional notification
   */
  handleError(error: unknown, showNotification: boolean = true): AuthError {
    const authError = this.parseError(error);

    if (showNotification && authError.showNotification) {
      this.showErrorNotification(authError);
    }

    return authError;
  }

  /**
   * Throw error as observable
   */
  throwAuthError(code: AuthErrorCode, originalError?: unknown): Observable<never> {
    const authError: AuthError = {
      code,
      message: AUTH_ERROR_MESSAGES[code],
      severity: AUTH_ERROR_SEVERITIES[code],
      category: AUTH_ERROR_CATEGORIES[code],
      recoveryAction: AUTH_ERROR_RECOVERY_ACTIONS[code],
      recoveryGuidance: AUTH_ERROR_RECOVERY_GUIDANCE[code],
      supportLink: this.config.supportUrl,
      originalError,
      timestamp: new Date(),
      shouldLog: true,
      showNotification: shouldShowNotification(code)
    };

    this.logError(authError);

    return throwError(() => authError);
  }

  /**
   * Type guard for AuthError
   */
  isAuthError(error: unknown): error is AuthError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      'message' in error &&
      'severity' in error &&
      'category' in error
    );
  }

  /**
   * Log error with context
   */
  private logError(error: AuthError): void {
    const logData: Record<string, unknown> = {
      code: error.code,
      category: error.category,
      severity: error.severity,
      statusCode: error.statusCode,
      requestId: error.requestId,
      timestamp: error.timestamp.toISOString(),
      message: error.technicalMessage || error.message
    };

    // SENTRY-NOISE-CLUSTER: fold the sanitized original error into THIS entry's
    // metadata instead of emitting a SECOND error-level log below. Two separate
    // logger.error() calls for one fault forwarded to Sentry as two distinct
    // captureException events (their messages differ — '[label] CODE' vs
    // '[label] CODE Original error:' — so they even split into two issues). One
    // entry carrying the original error in its extras is the same signal, once.
    if (this.config.logErrorsToConsole && error.originalError) {
      logData['originalError'] = sanitizeErrorForLogging(error.originalError);
    }

    // ADMIN-INTERFACE-61: a handled 401/403 (e.g. PERMISSION_DENIED, category
    // AUTHORIZATION) is expected RBAC behavior, not an actionable fault. Log it
    // at debug — below the threshold in prod(error) AND staging(warn) — so it
    // never reaches Sentry even if the layer-1 filter regressed. Genuine
    // ERROR-severity faults (NETWORK_ERROR, SERVER_ERROR, SERVICE_UNAVAILABLE,
    // ACCOUNT_LOCKED, UNKNOWN, …) stay at error. The UI toast (showErrorNotification)
    // is unaffected — the operator still sees the denial.
    const isHandledAuthDenial =
      error.category === AuthErrorCategory.AUTHORIZATION ||
      error.statusCode === 401 ||
      error.statusCode === 403;

    // ADMIN-INTERFACE-61: route faults through a category-accurate label and a
    // fingerprint-distinct message. Previously EVERY fault funneled through this
    // service logged the STATIC strings '[AuthError]' / '[AuthError] Original
    // error:'. The Sentry log handler fingerprints on the message string
    // (captureException(new Error(entry.message))), so this (1) mislabeled backend
    // 500s and network faults as "AuthError" — the operator-facing "permission
    // denied" misperception — and (2) collapsed every unrelated failure into ONE
    // Sentry issue (608 occurrences spanning /ml-anomaly-detection, /auth/login,
    // /ml-monitoring, …). Label by category and append code + status so a 500 now
    // reads '[ApiError] SERVER_ERROR (500)' and distinct faults form distinct
    // issues. The AUTHORIZATION branch keeps the '[AuthError]' prefix so the
    // layer-1 noise filter (isExpectedHandledNoise: startsWith('[AuthError]') +
    // category 'authorization') and the handled-401/403 debug downgrade below are
    // unaffected.
    const label =
      error.category === AuthErrorCategory.SERVER ? '[ApiError]'
      : error.category === AuthErrorCategory.NETWORK ? '[NetworkError]'
      : '[AuthError]';
    const detail = `${label} ${error.code}${error.statusCode ? ` (${error.statusCode})` : ''}`;

    switch (error.severity) {
      case AuthErrorSeverity.CRITICAL:
        this.logger.error(`${detail} Critical`, logData);
        break;
      case AuthErrorSeverity.ERROR:
        if (isHandledAuthDenial) {
          this.logger.debug(detail, logData);
        } else {
          this.logger.error(detail, logData);
        }
        break;
      case AuthErrorSeverity.WARNING:
        this.logger.warn(detail, logData);
        break;
      case AuthErrorSeverity.INFO:
        this.logger.info(detail, logData);
        break;
    }

    // Track with telemetry if enabled (non-blocking to avoid affecting error handling)
    if (this.useTelemetry) {
      queueMicrotask(() => {
        try {
          this.telemetryService.trackAuthError(error);
        } catch (telemetryError) {
          // Swallow telemetry errors - don't break error handling
          this.logger.warn('[AuthError] Telemetry tracking failed', {
            error: telemetryError instanceof Error ? telemetryError.message : String(telemetryError)
          });
        }
      });
    }
  }

  /**
   * Get support contact info
   */
  getSupportInfo(): { email: string; url: string } {
    return {
      email: this.config.supportEmail,
      url: this.config.supportUrl
    };
  }

  /**
   * Format unlock time for display
   */
  formatUnlockTime(error: AuthError): string | null {
    if (!error.unlockTime) {
      return null;
    }

    const now = new Date();
    const diff = error.unlockTime.getTime() - now.getTime();

    if (diff <= 0) {
      return 'now';
    }

    const minutes = Math.ceil(diff / 60000);
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours === 1 ? '' : 's'}`;
    }

    return `${hours} hour${hours === 1 ? '' : 's'} and ${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}`;
  }

  /**
   * Format retry time for display
   */
  formatRetryTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds} second${seconds === 1 ? '' : 's'}`;
    }

    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  /**
   * Get unlock time in milliseconds remaining (for countdown timers)
   * Returns 0 if not locked or already unlocked
   */
  getUnlockTimeRemaining(error: AuthError): number {
    if (!error.unlockTime) {
      return 0;
    }

    const now = new Date();
    const diff = error.unlockTime.getTime() - now.getTime();

    return Math.max(0, diff);
  }

  /**
   * Format countdown time as MM:SS
   */
  formatCountdown(milliseconds: number): string {
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // ============================================================
  // Telemetry Methods
  // ============================================================

  /**
   * Enable or disable telemetry tracking
   */
  setTelemetryEnabled(enabled: boolean): void {
    this.useTelemetry = enabled;
    if (enabled) {
      this.telemetryService.enable();
    } else {
      this.telemetryService.disable();
    }
  }

  /**
   * Check if telemetry is enabled
   */
  isTelemetryEnabled(): boolean {
    return this.useTelemetry && this.telemetryService.isEnabled();
  }

  /**
   * Get telemetry service instance for advanced usage
   * Use with caution - prefer using AuthErrorService methods
   */
  getTelemetryService(): AuthTelemetryService {
    return this.telemetryService;
  }

  /**
   * Track a successful authentication action
   */
  trackAuthSuccess(action: string, userId?: string, duration?: number): void {
    if (this.useTelemetry) {
      this.telemetryService.trackAuthSuccess(action, userId, duration);
    }
  }

  /**
   * Track error recovery attempt
   */
  trackRecovery(error: AuthError, recoveryAction: string, success: boolean): void {
    if (this.useTelemetry) {
      this.telemetryService.trackRecovery(error, recoveryAction, success);
    }
  }

  /**
   * Get current telemetry metrics
   */
  getTelemetryMetrics(): ReturnType<AuthTelemetryService['getMetrics']> {
    return this.telemetryService.getMetrics();
  }

  /**
   * Get error count for a specific error code
   */
  getTelemetryErrorCount(code: AuthErrorCode): number {
    return this.telemetryService.getErrorCount(code);
  }

  /**
   * Reset telemetry data
   */
  resetTelemetry(): void {
    this.telemetryService.clearData();
  }
}
